import { ref } from 'vue';

import {
  DOCUMENT_SEND_PDF_GENERIC_MESSAGE,
  documentSendApi,
  type DocumentSendRecipientReportParams,
} from '@/entities/DocumentSend';
import { printPdfBlob, shouldUsePrintFallbackWindow, useCustomToast } from '@/shared/lib';

export function useDocumentSendPdf() {
  const { showErrorToast } = useCustomToast();
  const isGenerating = ref(false);

  async function run(generate: () => Promise<Blob>): Promise<void> {
    if (isGenerating.value) {
      return;
    }

    isGenerating.value = true;

    const fallbackWindow = shouldUsePrintFallbackWindow() ? window.open('', '_blank') : null;

    try {
      const blob = await generate();
      printPdfBlob(blob, fallbackWindow);
    } catch {
      fallbackWindow?.close();
      showErrorToast({
        title: DOCUMENT_SEND_PDF_GENERIC_MESSAGE,
        type: 'error',
      });
    } finally {
      isGenerating.value = false;
    }
  }

  async function printDetail(id: number): Promise<void> {
    return run(() => documentSendApi.getDetailPdf(id));
  }

  async function printReportByDocument(documentRevisionId: number): Promise<void> {
    return run(() => documentSendApi.getReportByDocumentPdf(documentRevisionId));
  }

  async function printReportByRecipient(params: DocumentSendRecipientReportParams): Promise<void> {
    return run(() => documentSendApi.getReportByRecipientPdf(params));
  }

  return {
    isGenerating,
    printDetail,
    printReportByDocument,
    printReportByRecipient,
  };
}
